<template>
	<div class="k-input" :class="[{
		'k-input__disabled': disabled,
		'k-input__no-details': hideDetails,
	}]">
		<div v-if="separateLabel" class="k-input__label">{{ separateLabel }}</div>
		<v-text-field
			:id="id"
			v-model="value"
			:label="label"
			:placeholder="placeholder"
			:type="type"
			:min="min"
			:max="max"
			:disabled="disabled"
			class="rounded-0 k-input__input"
			:single-line="!variant"
			:variant="variant"
			:error-messages="errorMessage"
			:required="required"
			:clearable="clearable"
			:hint="hint"
			:persistent-hint="persistentHint"
			@change="change"
			:step="step"
			:persistent-clear="persistentClear"
		>
			<template v-if="appendInner" #append-inner>
				<slot name="appendInner"></slot>
			</template>
			<template v-if="append" #append>
				<slot name="append"></slot>
			</template>
			<template v-if="prependInner" #prepend-inner>
				<slot name="prependInner"></slot>
			</template>
			<template v-if="prepend" #prepend>
				<slot name="prepend"></slot>
			</template>
		</v-text-field>
	</div>
</template>

<script setup lang="ts">
import { computed, defineEmits, defineProps, toRef, withDefaults, watch } from 'vue';
import { useField } from 'vee-validate';

interface IOwnProps {
	label?: string
	hint?: string
	modelValue?: string | number | null
	name?: string
	placeholder?: string
	type?: string
	separateLabel?: string
	variant?: 'filled' | 'outlined' | 'plain' | 'underlined' | 'solo' | 'solo-inverted' | 'solo-filled' | undefined
	min?: string | number
	max?: string | number
	id?: string
	step?: string
	required?: boolean
	disabled?: boolean
	clearable?: boolean
	persistentHint?: boolean
	persistentClear?: boolean
	isCustomClear?: boolean
	hideDetails?: boolean
	validateOnValueUpdate?: boolean
	appendInner?: boolean
	append?: boolean
	prependInner?: boolean
	prepend?: boolean
	change?: Function
}
interface IProps extends IOwnProps {
}

const props = withDefaults(defineProps<IProps>(), {
    variant: 'outlined',
    validateOnValueUpdate: true,
});
const emit = defineEmits(['update:modelValue']);

const { value: fieldValue, errorMessage } = useField(toRef(props, 'name'), undefined, {
	initialValue: props.modelValue,
	validateOnValueUpdate: props.validateOnValueUpdate,
});

watch(() => props.modelValue, (newValue) => {
    if (newValue !== fieldValue.value) {
        fieldValue.value = newValue;
    }
}, { immediate: true });

const value = computed({
    get() {
        return fieldValue.value;
    },
    set(newValue) {
        fieldValue.value = newValue;
        emit('update:modelValue', newValue);
    }
});
</script>

<style scoped lang="scss">
.k-input {
	width: 100%;

	&__label {
		color: $black;
		font-weight: 300;
		font-size: 14px;
		margin-bottom: 3px;
	}

	&__disabled {
		cursor: no-drop !important;
		&:deep(fieldset) {
			border: none;
		}

		&:deep(.v-input__control) {
			background-color: $grey-400;
		}
	}

	&__no-details {
		&:deep(.v-input__details) {
			display: none;
		}
	}

	&:deep(.v-input input) {
		min-height: 41px;
		max-height: 41px;
		padding: 4px 12px 4px 17px;
		font-size: 14px;
		background-color: transparent;
		border-radius: 5px;
		font-weight: 300;
	}
	// To ponizej koloruje vuetifeyowy border ktory jest jakis tam domylsny
	:deep(.v-field__outline) {
		color: $black;
	}

	:deep(.v-text-field .v-input__details) {
		padding-inline-start: 0;
		min-height: 16px;
		padding-top: 4px;
		padding-bottom: 4px;
	}

	:deep(.v-text-field) {
		padding-top: 0 !important;
        color: $grey-400;
	}

	:deep(.v-application--is-ltr .v-text-field .v-label) {
		left: 0 !important;
	}

	:deep(.v-text-field.v-text-field--enclosed:not(.v-text-field--rounded) > .v-input__control > .v-input__slot) {
		padding: 0;
	}

    :deep(input::placeholder) {
        color: $black;
        opacity: 0.5;
    }
}
</style>
